import { useState, useEffect } from 'react'
import { AdminSettingsService } from '../services/adminSettings'
import {
  AI_MODELS,
  TEXT_VERBOSITY_OPTIONS,
  reasoningEffortOptions,
  supportsReasoning,
  supportsTemperature,
} from '../constants/aiModels'
import {
  AI_RESPONSE_POLICY_DEFAULT_INSTRUCTIONS,
  AI_RESPONSE_POLICY_OPTIONS,
  AI_RESPONSE_POLICY_SETTING_KEYS,
  type AiResponsePolicy,
} from '../constants/aiResponsePolicy'
import type { AdminSettingKey } from '../types/adminSettings'

interface PolicyFields {
  label: string
  instruction: string
}

type PolicyState = Record<AiResponsePolicy, PolicyFields>

interface BaseState {
  homeworkChatPrompt: string
  model: string
  maxTokens: string
  temperature: string
  reasoningEffort: string
  textVerbosity: string
  portfolioPrompt: string
  portfolioModel: string
  portfolioMaxTokens: string
}

const BASE_KEYS: Record<keyof BaseState, AdminSettingKey> = {
  homeworkChatPrompt: 'homework_chat_prompt_base',
  model: 'ai_default_model',
  maxTokens: 'ai_max_tokens',
  temperature: 'ai_temperature',
  reasoningEffort: 'ai_reasoning_effort',
  textVerbosity: 'ai_text_verbosity',
  portfolioPrompt: 'portfolio_prompt',
  portfolioModel: 'portfolio_ai_model',
  portfolioMaxTokens: 'portfolio_max_tokens',
}

// homework_chat_prompt_base must stay readable by students (they render the homework
// chat client-side); the rest are only read server-side with the service role.
const PUBLIC_KEYS: ReadonlySet<AdminSettingKey> = new Set<AdminSettingKey>([
  'homework_chat_prompt_base',
])

// Shown as placeholders so the admin can see what the AI receives when a field is blank.
const DEFAULT_INSTRUCTIONS = AI_RESPONSE_POLICY_DEFAULT_INSTRUCTIONS

const emptyPolicies = (): PolicyState => ({
  think_first: { label: '', instruction: '' },
  hint_first: { label: '', instruction: '' },
  direct: { label: '', instruction: '' },
})

const emptyBase = (): BaseState => ({
  homeworkChatPrompt: '',
  model: '',
  maxTokens: '',
  temperature: '',
  reasoningEffort: '',
  textVerbosity: '',
  portfolioPrompt: '',
  portfolioModel: '',
  portfolioMaxTokens: '',
})

const asText = (value: unknown): string => (typeof value === 'string' ? value : '')

const asNumberText = (value: unknown): string => {
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string') return value
  return ''
}

const AiSettingsManager = () => {
  const [base, setBase] = useState<BaseState>(emptyBase())
  const [policies, setPolicies] = useState<PolicyState>(emptyPolicies())
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    const load = async () => {
      setIsLoading(true)
      const result = await AdminSettingsService.getSettingsByCategory('ai')
      if (result.success) {
        const map = new Map<string, unknown>(result.data.map(s => [s.key, s.value]))

        setBase({
          homeworkChatPrompt: asText(map.get(BASE_KEYS.homeworkChatPrompt)),
          model: asText(map.get(BASE_KEYS.model)),
          maxTokens: asNumberText(map.get(BASE_KEYS.maxTokens)),
          temperature: asNumberText(map.get(BASE_KEYS.temperature)),
          reasoningEffort: asText(map.get(BASE_KEYS.reasoningEffort)),
          textVerbosity: asText(map.get(BASE_KEYS.textVerbosity)),
          portfolioPrompt: asText(map.get(BASE_KEYS.portfolioPrompt)),
          portfolioModel: asText(map.get(BASE_KEYS.portfolioModel)),
          portfolioMaxTokens: asNumberText(map.get(BASE_KEYS.portfolioMaxTokens)),
        })

        const nextPolicies = emptyPolicies()
        for (const option of AI_RESPONSE_POLICY_OPTIONS) {
          const keys = AI_RESPONSE_POLICY_SETTING_KEYS[option.value]
          nextPolicies[option.value] = {
            label: asText(map.get(keys.label)),
            instruction: asText(map.get(keys.instruction)),
          }
        }
        setPolicies(nextPolicies)
      } else {
        setMessage({ type: 'error', text: '設定の読み込みに失敗しました' })
      }
      setIsLoading(false)
    }
    load()
  }, [])

  const handleBaseChange = (field: keyof BaseState, value: string) => {
    setBase(prev => ({ ...prev, [field]: value }))
  }

  const handlePolicyChange = (policy: AiResponsePolicy, field: keyof PolicyFields, value: string) => {
    setPolicies(prev => ({ ...prev, [policy]: { ...prev[policy], [field]: value } }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    setMessage(null)

    const updates: Array<{ key: AdminSettingKey; value: string | number }> = [
      { key: BASE_KEYS.homeworkChatPrompt, value: base.homeworkChatPrompt },
      { key: BASE_KEYS.model, value: base.model },
      // Blank is a real choice here: it means "send no reasoning/verbosity field and
      // let the model decide", so it is stored rather than skipped.
      { key: BASE_KEYS.reasoningEffort, value: base.reasoningEffort },
      { key: BASE_KEYS.textVerbosity, value: base.textVerbosity },
      { key: BASE_KEYS.portfolioPrompt, value: base.portfolioPrompt },
      { key: BASE_KEYS.portfolioModel, value: base.portfolioModel },
    ]

    // Numbers are stored as JSON numbers. A blank or unparseable field leaves the stored
    // value alone rather than writing a bogus 0.
    const maxTokens = parseInt(base.maxTokens, 10)
    if (Number.isFinite(maxTokens)) {
      updates.push({ key: BASE_KEYS.maxTokens, value: maxTokens })
    }
    const temperature = parseFloat(base.temperature)
    if (Number.isFinite(temperature)) {
      updates.push({ key: BASE_KEYS.temperature, value: temperature })
    }
    const portfolioMaxTokens = parseInt(base.portfolioMaxTokens, 10)
    if (Number.isFinite(portfolioMaxTokens)) {
      updates.push({ key: BASE_KEYS.portfolioMaxTokens, value: portfolioMaxTokens })
    }

    for (const option of AI_RESPONSE_POLICY_OPTIONS) {
      const keys = AI_RESPONSE_POLICY_SETTING_KEYS[option.value]
      updates.push({ key: keys.label, value: policies[option.value].label })
      updates.push({ key: keys.instruction, value: policies[option.value].instruction })
    }

    // Try update first; if the row doesn't exist yet, create it
    const errors: string[] = []
    for (const u of updates) {
      const updateResult = await AdminSettingsService.updateSetting(u.key, u.value)
      if (!updateResult.success) {
        const createResult = await AdminSettingsService.createSetting(
          u.key, u.value, undefined, 'ai', PUBLIC_KEYS.has(u.key)
        )
        if (!createResult.success) {
          errors.push(u.key)
        }
      }
    }

    setIsSaving(false)
    if (errors.length === 0) {
      setMessage({ type: 'success', text: '保存しました' })
    } else {
      setMessage({ type: 'error', text: '一部の設定の保存に失敗しました' })
    }
    setTimeout(() => setMessage(null), 3000)
  }

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  // Which knobs apply is a property of the model, so the fields the chosen model ignores
  // are disabled here rather than silently dropped at request time. This reflects the
  // default model only: a みらいワーク with its own model can still land on the other set.
  const selectedModel = base.model
  const selectedModelLabel =
    AI_MODELS.find(m => m.value === selectedModel)?.label || selectedModel || '選択中のモデル'
  const temperatureIgnored = !!selectedModel && !supportsTemperature(selectedModel)
  const reasoningSupported = !!selectedModel && supportsReasoning(selectedModel)

  // The reasoning scale differs per model, and OpenAI rejects the whole request for a
  // value the model does not know. A stored effort the model has dropped is kept in the
  // list and flagged instead of vanishing, so switching models cannot silently rewrite
  // the setting on the next save.
  const reasoningOptions = reasoningEffortOptions(selectedModel)
  const reasoningEffortUnsupported =
    reasoningSupported &&
    !!base.reasoningEffort &&
    !reasoningOptions.some(o => o.value === base.reasoningEffort)

  // The portfolio batch has its own model, so its notice is computed separately from the
  // chat's. Temperature there is a fixed 0.3 in the edge function rather than a setting,
  // so the only knob worth warning about is the token budget.
  const portfolioModelLabel =
    AI_MODELS.find(m => m.value === base.portfolioModel)?.label || base.portfolioModel || '選択中のモデル'
  const portfolioModelIsReasoning = !!base.portfolioModel && supportsReasoning(base.portfolioModel)

  const inputClass = 'w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
  const textareaClass = `${inputClass} resize-y font-mono`

  return (
    <div className="p-6 max-w-3xl">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900">AI設定</h2>
        <p className="mt-1 text-sm text-gray-500">
          AIチャットで使用するプロンプトとモデル、キャリアサマリ生成の設定、および学校ごとに選べる回答方針を設定します。
        </p>
      </div>

      <div className="mb-8 border border-gray-200 rounded-lg p-5">
        <h3 className="text-base font-medium text-gray-800 mb-4">基本設定</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              宿題チャットプロンプト
            </label>
            <p className="text-xs text-gray-500 mb-2">
              宿題のAIチャットで使用するシステムプロンプトです。
            </p>
            <textarea
              value={base.homeworkChatPrompt}
              onChange={e => handleBaseChange('homeworkChatPrompt', e.target.value)}
              rows={10}
              className={textareaClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              デフォルトAIモデル
            </label>
            <p className="text-xs text-gray-500 mb-2">
              使用するAIモデルです。
            </p>
            <select
              value={base.model}
              onChange={e => handleBaseChange('model', e.target.value)}
              className="w-full sm:w-72 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              {AI_MODELS.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              最大トークン数
            </label>
            <p className="text-xs text-gray-500 mb-2">
              AI応答の最大トークン数です。
            </p>
            <input
              type="number"
              value={base.maxTokens}
              onChange={e => handleBaseChange('maxTokens', e.target.value)}
              min={100}
              max={4000}
              step={100}
              className="w-full sm:w-72 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              温度パラメータ（GPT-4 シリーズのみ）
            </label>
            <p className="text-xs text-gray-500 mb-2">
              AIの創造性レベル (0.0-2.0) です。
            </p>
            <input
              type="number"
              value={base.temperature}
              onChange={e => handleBaseChange('temperature', e.target.value)}
              min={0}
              max={2}
              step={0.1}
              disabled={temperatureIgnored}
              className={`w-full sm:w-72 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${temperatureIgnored ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
            />
            {temperatureIgnored && (
              <p className="mt-1 text-xs text-amber-700">
                {selectedModelLabel} は温度パラメータに対応していないため、この値は送信されません。
                応答の長さや踏み込み具合は下の2項目で調整してください。
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              推論の深さ（GPT-5 シリーズのみ）
            </label>
            <p className="text-xs text-gray-500 mb-2">
              回答前にどれだけ考えるかです。低いほどプロンプトに忠実で短い応答になり、高いほど先の工程まで踏み込んだ応答になりやすくなります。
            </p>
            <select
              value={base.reasoningEffort}
              onChange={e => handleBaseChange('reasoningEffort', e.target.value)}
              disabled={!reasoningSupported}
              className={`w-full sm:w-72 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${reasoningSupported ? '' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
            >
              {reasoningOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
              {reasoningEffortUnsupported && (
                <option value={base.reasoningEffort}>{base.reasoningEffort}（非対応）</option>
              )}
            </select>
            {reasoningEffortUnsupported && (
              <p className="mt-1 text-xs text-amber-700">
                {selectedModelLabel} は「{base.reasoningEffort}」に対応していません。
                このまま保存すると値は送信されず、モデル既定の深さで動作します。
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              応答の詳しさ（GPT-5 シリーズのみ）
            </label>
            <p className="text-xs text-gray-500 mb-2">
              1回の応答の長さです。
            </p>
            <select
              value={base.textVerbosity}
              onChange={e => handleBaseChange('textVerbosity', e.target.value)}
              disabled={!reasoningSupported}
              className={`w-full sm:w-72 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${reasoningSupported ? '' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
            >
              {TEXT_VERBOSITY_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {!reasoningSupported && (
              <p className="mt-1 text-xs text-amber-700">
                {selectedModelLabel} は推論モデルではないため、上の2項目は送信されません。
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mb-8 border border-gray-200 rounded-lg p-5">
        <h3 className="text-base font-medium text-gray-800 mb-4">キャリアサマリ生成設定</h3>
        <p className="text-sm text-gray-500 mb-4">
          生徒のキャリアサマリを自動生成するバッチの設定です。生成対象のデータ（AIサマリー・宿題の提出記録）は自動で付加されるため、ここには役割と出力形式のみを記述します。
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              キャリアサマリ生成プロンプト
            </label>
            <p className="text-xs text-gray-500 mb-2">
              キャリアサマリ生成AIのシステムプロンプトです。出力形式のJSONキー名（interests / strengths / career_vision / achievements / growth / advice）は保存先の項目と対応しているため固定です。キー名を変更・追加・削除しても保存される項目は変わりません。空欄にすると初期プロンプトが使用されます。
            </p>
            <textarea
              value={base.portfolioPrompt}
              onChange={e => handleBaseChange('portfolioPrompt', e.target.value)}
              rows={18}
              className={textareaClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              キャリアサマリ生成AIモデル
            </label>
            <p className="text-xs text-gray-500 mb-2">
              キャリアサマリ生成で使用するAIモデルです。
            </p>
            <select
              value={base.portfolioModel}
              onChange={e => handleBaseChange('portfolioModel', e.target.value)}
              className="w-full sm:w-72 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              {AI_MODELS.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              キャリアサマリ生成の最大トークン数
            </label>
            <p className="text-xs text-gray-500 mb-2">
              生徒1人あたりの最大出力トークン数です。6項目×200字で約1,500トークン必要なため、小さくしすぎると出力がJSONの途中で切れて生成に失敗します。
            </p>
            <input
              type="number"
              value={base.portfolioMaxTokens}
              onChange={e => handleBaseChange('portfolioMaxTokens', e.target.value)}
              min={500}
              max={8000}
              step={100}
              className="w-full sm:w-72 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
            {portfolioModelIsReasoning && (
              <p className="mt-1 text-xs text-amber-700">
                {portfolioModelLabel} は推論モデルのため、このトークン数には回答前の思考分も含まれます。出力分だけでは足りないので、余裕を持った値（4000以上、初期値は6000）を設定してください。
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-base font-medium text-gray-800">回答方針</h3>
        <p className="mt-1 text-sm text-gray-500">
          学校ごとに選べるAIの回答方針です。表示タイトルは学校設定の選択肢として先生に表示され、AIへの指示はその方針を選んだ学校のAIチャットに追加されます。
          空欄にすると初期値（プレースホルダーの内容）が使用されます。
        </p>
      </div>

      <div className="space-y-8">
        {AI_RESPONSE_POLICY_OPTIONS.map((option, index) => (
          <div key={option.value} className="border border-gray-200 rounded-lg p-5">
            <h4 className="text-base font-medium text-gray-800 mb-4">
              選択肢{index + 1}
            </h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  表示タイトル
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  学校設定の「AIの回答方針」で先生に表示される選択肢のテキストです。
                </p>
                <input
                  type="text"
                  value={policies[option.value].label}
                  onChange={e => handlePolicyChange(option.value, 'label', e.target.value)}
                  placeholder={option.label}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  AIへの指示
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  この方針を選んだ学校のAIチャットで、システムプロンプトの「【回答方針】」として使用されます。
                </p>
                <textarea
                  value={policies[option.value].instruction}
                  onChange={e => handlePolicyChange(option.value, 'instruction', e.target.value)}
                  rows={4}
                  placeholder={DEFAULT_INSTRUCTIONS[option.value]}
                  className={`${inputClass} resize-y`}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-4">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-6 py-2 rounded-md text-sm font-medium"
        >
          {isSaving ? '保存中...' : '保存'}
        </button>
        {message && (
          <span className={`text-sm font-medium ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
            {message.text}
          </span>
        )}
      </div>
    </div>
  )
}

export default AiSettingsManager
